<template>
  <div class="gw-start f-h f-w">
    <FiltersZone no-options-button>
      <div slot="shortcuts">
        <Switcher
          v-if="oldGwId"
          v-model="activeTab"
          :titles="tabTitles"
          @input="tabChange"
        />
      </div>
    </FiltersZone>
    <div v-show="activeTab === 0" class="gw-start__wrapper f-h-below-filters">
      <!-- Description panel -->
      <div class="gw-start__basics generic-border">
        <h1 class="basic-blue-title">
          {{ $t("message.DESCRIPTION") }}
        </h1>
        <form class="container">
          <EditableLabel
            v-model="stateGwCopy.name"
            class="mt10"
            :legend="$t('message.GW_NAME') + $t('message.TWO_DOTS')"
            :placeholder="$t('message.GW_NAME_PLACEHOLDER')"
            @input="save"
          />
          <EditableLabel
            v-model="stateGwCopy.objective"
            class="mt10"
            :legend="$t('message.GW_OBJECTIVE') + $t('message.TWO_DOTS')"
            :placeholder="$t('message.GW_OBJECTIVE_PLACEHOLDER')"
            @input="save"
          />
          <EditableLabelRadio
            v-model="stateGwCopy.participants"
            class="mt10"
            label-key="fullName"
            separator=", "
            :legend="$t('message.GW_PARTICIPANTS') + $t('message.TWO_DOTS')"
            :items="userItems"
            multiple
            @input="save"
          />
          <IntCheckbox
            v-model="stateGwCopy.shouldSetAuditedUser"
            :title="$t('message.GW_SHOULD_SET_AUDITED_USER')"
            @input="save"
          />
        </form>
        <div class="h-v-centerer">
          <button class="generic-border generic-border--text-btn generic-border--blue" @click="onStart">
            {{ $t('message.START') }}
          </button>
        </div>
      </div>
      <!-- Steps panel -->
      <GwEditionSteplist :gemba-walk="stateGwCopy" class="gw-start__steps generic-border" />
    </div>
    <div v-show="activeTab === 1" class="f-h-below-filters">
      <GwCompleted ref="summary" :gemba-walk-id="oldGwId" :embed="true" />
    </div>
  </div>
</template>

<script>
/**
 * Page allowing the user to edit the GW before starting it in order to have
 * a field tour slightly different from the template.
 * When the user reaches this page, a new GW is created, just as if it has been started.
 * The GW is deleted if the user leaves the page without starting it.
 */

import toastr from 'toastr';
import { isEqual, cloneDeep, pick } from 'lodash';
import { createNamespacedHelpers, mapState } from 'vuex';
// Vuex
import { FETCH_TEAMS } from '@/store/action-types';
import { FETCH_GW, SET_GW, START_GW, DELETE_GW } from '@/store/modules/gemba-walks/action-types';
import { GET_TEAMS } from '@/store/mutation-types';
// Components
import EditableLabel from '@/components/editable-label.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import FiltersZone from '@/components/filters-zone.vue';
import GwCompleted from '@/views/gw/completed.vue';
import GwEditionSteplist from '@/components/gw-edition-steplist.vue';
import IntCheckbox from '@/components/int-checkbox.vue';
import Switcher from '@/components/switcher.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
// Utils
import IAHelpStep from '@/utils/classes/IAHelpStep';
import { calert } from '@/utils/main';

const { mapActions: gwMapActions } = createNamespacedHelpers('gembaWalks');

// Function that give a default gw object
function createGWData() {
  return {
    id: 0,
    name: '',
    objective: '',
    participants: [],
    steps: [],
  };
}

export default {

  name: 'GwStart',

  components: {
    EditableLabel,
    EditableLabelRadio,
    FiltersZone,
    GwCompleted,
    GwEditionSteplist,
    IntCheckbox,
    Switcher,
  },

  mixins: [mixinSocketHandler],

  data() {
    return {
      suppressLeaveWarning: false,
      // Tabs
      activeTab: 0,
      tabTitles: [this.$t('message.GW_NEW'), this.$t('message.GW_OLD')],
      // Form data
      gw: createGWData(),
      stateGwCopy: {},
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.GW_EDIT_START'),
      pageBackButtonAction: () => {
        this.$router.back();
      },
    };
  },

  computed: {
    userItems() {
      const currentTeamString = this.$t('message.OUR_TEAM');
      const currentTeamId = (this.App.user ? this.App.user.id : null);
      const userItems = [];
      this.teamsData.forEach((team) => {
        // Create a category for each team in the editable-label-radio component
        // => Format each user item
        const teamItem = {
          title: team.name + (team.id === currentTeamId ? ` (${currentTeamString})` : ''),
          radioItems: team.users.map(user => ({
            id: user.id,
            fullName: `${user.firstName} ${user.lastName}`,
            teamId: team.id,
          })),
        };
        // Make the current team always at the top
        if (team.id === currentTeamId) {
          userItems.unshift(teamItem);
        } else {
          userItems.push(teamItem);
        }
      });
      return userItems;
    },
    oldGwId() {
      return this.stateGwCopy && this.stateGwCopy.prevId ? Number(this.stateGwCopy.prevId) : 0;
    },
    /** Number: ID of the GW template in the route params */
    templateId() {
      return this.$route.params.templateId;
    },
    /** Number: ID of the GW started to be edited in this page */
    gwId() {
      return Number(this.$route.query.gwId || 0);
    },
    /** Object: GW from state */
    stateGw() {
      return this.$store.state.gembaWalks.gwPerId[`${this.gwId}`] || {};
    },
    ...mapState({
      teamsData: state => state[GET_TEAMS.stateKey] || [],
      teamsDataLoading: state => state[GET_TEAMS.loadingKey],
    }),
  },

  watch: {
    stateGw: {
      deep: true,
      handler() {
        this.stateGwCopy = cloneDeep(this.stateGw);
      },
    },
  },

  created() {
    // Fetch the GW specified in the params and check if it exists
    // If so, start it to have a new instance to edit
    if (this.gwId > 0) {
      this.FETCH_GW({ id: this.gwId })
        .then((gw) => {
          if (!gw || !(gw instanceof Object) || gw.id !== this.gwId) {
            this.createGwInstance();
          }
        })
        .catch(this.createGwInstance);
    } else {
      this.createGwInstance();
    }
    if (!this.teamsDataLoading) {
      this.$store.dispatch(FETCH_TEAMS);
    }
  },

  beforeRouteLeave(to, from, next) {
    this.warnLeave((nextParams) => {
      next(nextParams);
    });
  },

  methods: {
    /** Create a new instance of GW to allow editing it before carrying it out */
    createGwInstance() {
      this.START_GW({ template: this.templateId })
        .then((response) => {
          if (response && response.data instanceof Object) {
            const query = Object.assign({}, this.$route.query);
            query.gwId = response.data.id;
            if (!isEqual(query, this.$route.query)) {
              this.$router.replace({ query });
            }
          }
        })
        .catch(() => {
          // GW not created, go back
          this.suppressLeaveWarning = true;
          this.$router.back();
        });
    },
    /** Save GW details, such as name and objective */
    save() {
      this.SET_GW(pick(this.stateGwCopy, 'id', 'name', 'objective', 'participants', 'shouldSetAuditedUser'))
        .then((response) => {
          if (response && response.data instanceof Object) {
            toastr.success(this.$t('message.GW_SAVED'));
          }
        });
    },
    /** Switching between tabs */
    tabChange() {
      if (this.activeTab === 1) {
        this.$refs.summary.refresh();
      }
    },
    warnLeave(next) {
      if (this.suppressLeaveWarning) {
        next();
        return;
      }
      calert(this.$t('message.GW_LEAVE_START'), [
        {
          title: this.$t('message.CONTINUE'),
          action: () => {
            this.DELETE_GW({ id: this.gwId }).then(next).catch(next);
          },
          sClass: 'red',
        },
        {
          title: this.$t('message.CANCEL'),
          action() {
            next(false);
          },
        },
      ]);
    },
    /** Redirect to the ongoing GW */
    onStart() {
      if (this.gwId) {
        this.suppressLeaveWarning = true;
        this.$router.push(this.getGwOngoingLink(this.gwId));
      }
    },
    createHelpSteps() {
      return [
        new IAHelpStep(null, this.$t('help.GW_START_INTRO')),
      ];
    },
    ...gwMapActions({
      SET_GW,
      FETCH_GW,
      START_GW,
      DELETE_GW,
    }),
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.gw-start {
  overflow: hidden;

  &__wrapper {
    display: flex;
    padding: $page-content-padding;
  }

  &__basics {
    flex: 0 0 30%;
    width: 30%;
    margin-right: 0.7rem;
    padding: 0 $page-content-padding;
  }

  &__steps {
    flex: 1;
    min-width: 0px;
    padding: 0 $page-content-padding;
  }
}
</style>
